<template>
  <div v-if="display" class="stats-sheet">
    <div v-if="!currentData.length">
      <p class="stats-sheet__no-data">{{ $t('message.STATS_NODATA') }}</p>
    </div>
    <div v-else>
      <div class="basic-search-field v-centerer">
        <Icon>{{ searchMedium }}</Icon>
        <input v-model="search" :placeholder="$t('message.SEARCH_PH')">
      </div>
      <table class="stats-sheet__table mt10em">
        <thead class="stats-sheet__table__head">
          <tr class="stats-sheet__table__head__titles">
            <th />
            <th v-if="dataType !== ('teams' || 'users')" colspan="2">{{ $t('message.ACTIONS') }}</th>
            <th v-if="dataType !== ('teams' || 'users')" colspan="2">{{ $t('message.PROBLEMS') }}</th>
            <th colspan="2">{{ $t('message.TIME') }}</th>
            <th colspan="1">{{ $t('message.CONNECTIONS') }}</th>
            <th v-if="dataType !== ('teams' || 'users')" colspan="2">{{ $t('message.AVERAGE_CREATION_TIME') }}</th>
            <th v-if="dataType !== ('teams' || 'users')" colspan="2">{{ $t('message.REPORTED_ON_JIRA') }}</th>
          </tr>
          <tr class="stats-sheet__table__head__subtitles">
            <th
              v-for="(col, key) in columnDetails"
              :key="key"
              :class="{ active: sortByKey === col.key, 'stats-sheet__border-right': col.rightBorder }"
              @click="changeSortKey(col.key)"
            >
              <span>{{ $t(`message.${col.trKey}`) }}</span>
              <span v-if="sortByKey === col.key && sortDesc">
                &#9660;
              </span>
              <span v-else-if="sortByKey === col.key">
                &#9650;
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(data, key) in limitedDataList" :key="key" @click="nextPath(data)">
            <td
              class="stats-sheet__border-right"
            >
              {{ dataType === ('teams' || 'users') ? data.username : data.name }}
            </td>
            <!-- Actions -->
            <td v-if="dataType !== ('teams' || 'users')">{{ data.sumNActionsCreated }}</td>
            <td v-if="dataType !== ('teams' || 'users')" class="stats-sheet__border-right">
              {{ data.sumNActionsFinished }}
            </td>
            <!-- Problems -->
            <td v-if="dataType !== ('teams' || 'users')">{{ data.sumNPbCreated }}</td>
            <td
              v-if="dataType !== ('teams' || 'users')"
              class="stats-sheet__border-right"
            >
              {{ data.sumNPbFinished }}
            </td>
            <!-- Online / Offline -->
            <td>{{ duration(data.sumOnlineTime) }}</td>
            <td class="stats-sheet__border-right">{{ duration(data.sumAfkTime) }}</td>
            <!-- Connections -->
            <td
              :class="dataType !== ('teams' || 'users') && `stats-sheet__border-right`"
            >
              {{ data.sumNConnection }}
            </td>
            <!-- Time to create an action/problem -->
            <td v-if="dataType !== ('teams' || 'users')">{{ duration(data.sumActionCreatingTime) }}</td>
            <td v-if="dataType !== ('teams' || 'users')" class="stats-sheet__border-right">
              {{ duration(data.sumPbCreatingTime) }}
            </td>
            <!-- Issues reported -->
            <td v-if="dataType !== ('teams' || 'users')">{{ data.sumNBugs }}</td>
            <td v-if="dataType !== ('teams' || 'users')">{{ data.sumNTasks }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="filteredDataList.length > limitedDataList.length" class="h-v-centerer">
      <button class="action-btn" @click="dataLimit += 30">
        {{ $t('message.LOAD_MORE') }}
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Array with currents users group stats
 *
 * @vue-prop {Array} [currentData=[]] - companies / teams / users data
 * @vue-prop {String} [dataType=''] - get the current type of data
 * @vue-prop {Boolean} [display=true] - display or hide the stats sheet as needed
 */

// Components
import Icon from '@/components/icon.vue';
// Utils & assets
import { secondsToArray } from '@/utils/main';
import { searchMedium } from '@/assets/icons';

export default {

  name: 'StatsSheet',

  components: {
    Icon,
  },

  props: {
    currentData: {
      type: Array,
      default: () => [],
    },
    dataType: {
      type: String,
      default: () => '',
    },
    display: {
      type: Boolean,
      default: () => true,
    },
  },

  data() {
    return {
      searchMedium,
      search: '',
      sortByKey: null,
      sortDesc: true,
      dataLimit: 30,
    };
  },

  computed: {
    /** String: Translation key of the type of data */
    dataTr() {
      switch (this.dataType) {
        case 'allDatas':
          return 'COMPANIES';
        case 'companies':
          return 'TEAMS';
        default:
          return 'USERS';
      }
    },
    /** Object: Return an object with data needed for the next page */
    nextPage() {
      switch (this.dataType) {
        case 'allDatas':
          return { name: 'shizenStatsCompany', id: 'companyId' };
        case 'companies':
          return { name: 'shizenStatsTeam', id: 'teamId' };
        default:
          return { name: 'shizenStatsUser', id: 'userId' };
      }
    },
    /** Boolean: Return whether the data is a list of a team or a user */
    isDataTeamsOrUsers() {
      return this.dataType === ('teams' || 'users');
    },
    /** String: Key of the name property */
    nameKey() {
      return this.isDataTeamsOrUsers ? 'username' : 'name';
    },
    /** Array: column displayed in the table */
    columnDetails() {
      const basicCols = [
        { key: this.nameKey, trKey: this.dataTr, rightBorder: true },
        { key: 'sumOnlineTime', trKey: 'ONLINE' },
        { key: 'sumAfkTime', trKey: 'ABSENT', rightBorder: true },
        { key: 'sumNConnection', trKey: 'CONNECTIONS' },
      ];
      if (this.dataType !== ('teams' || 'users')) {
        return [
          { key: this.nameKey, trKey: this.dataTr, rightBorder: true },
          { key: 'sumNActionsCreated', trKey: 'CREATED_ACTIONS' },
          { key: 'sumNActionsFinished', trKey: 'FINISHED_ACTIONS', rightBorder: true },
          { key: 'sumNPbCreated', trKey: 'CREATED_PROBLEMS' },
          { key: 'sumNPbFinished', trKey: 'FINISHED_PROBLEMS', rightBorder: true },
          { key: 'sumOnlineTime', trKey: 'ONLINE' },
          { key: 'sumAfkTime', trKey: 'ABSENT', rightBorder: true },
          { key: 'sumNConnection', trKey: 'CONNECTIONS', rightBorder: true },
          { key: 'sumActionCreatingTime', trKey: 'ACTION' },
          { key: 'sumPbCreatingTime', trKey: 'PROBLEM', rightBorder: true },
          { key: 'sumNBugs', trKey: 'JIRA_TRIGGER_BUGS_MOBILE' },
          { key: 'sumNTasks', trKey: 'SUGGESTIONS' },
        ];
      }
      return basicCols;
    },
    /** Array: Data filtered using the search bar */
    filteredDataList() {
      const effectiveSearch = this.search.sAccentless.toUpperCase().split(/[\s,-./']/);
      const filteredData = this.currentData.filter((team) => {
        const uppperName = (team.name || '').toUpperCase();
        const inName = !effectiveSearch.some(term => !uppperName.includes(term));
        const uppperUsername = (team.username || '').toUpperCase();
        const inUsername = !effectiveSearch.some(term => !uppperUsername.includes(term));
        return inName || inUsername;
      });

      if (this.sortByKey) {
        if (this.sortByKey === this.nameKey) {
          return filteredData.sort((item1, item2) => (
            this.sortDesc
              ? item2[this.sortByKey].localeCompare(item1[this.sortByKey])
              : item1[this.sortByKey].localeCompare(item2[this.sortByKey])));
        }
        return filteredData.sort((item1, item2) => (
          this.sortDesc
            ? item2[this.sortByKey] - item1[this.sortByKey]
            : item1[this.sortByKey] - item2[this.sortByKey]));
      }
      return filteredData;
    },
    /** Array: Data limited to 30 items by default */
    limitedDataList() {
      return this.filteredDataList.slice(0, this.dataLimit);
    },
  },

  watch: {
    $route(from, to) {
      if (from.path !== to.path) {
        this.search = '';
      }
    },
  },

  methods: {
    /** Return a string with the time duration in H m s */
    duration(entry) {
      const duration = Number(entry || 0);
      const time = secondsToArray(Math.round(duration));
      const timeString = `${time[0]}h ${time[1]}m ${time[2]}s`;
      if (time[3] > 0) {
        return `${time[3]}${this.$t('message.SHORT_DAY')} ${timeString}`;
      }
      return timeString;
    },
    /** Go to the detailed route */
    nextPath(userData) {
      this.$router.push({
        name: this.nextPage.name,
        params: { [this.nextPage.id]: userData.id },
        query: this.$route.query,
      });
    },
    /** Change the sorting option and sort using the key provided as the 1st argument */
    changeSortKey(sortKey) {
      if (this.sortByKey === sortKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortByKey = sortKey;
        this.sortDesc = true;
      }
    },
  },

};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.stats-sheet {
  position: relative;
  overflow-x: auto !important;
  background: white;
  &__no-data {
    color: red;
    text-align: center;
    font-weight: bold;
  }

  &__table {
    border-collapse: collapse;
    background: white;
    margin-bottom: 2em;

    tbody tr {
      cursor: pointer;

      &:hover {
        background: $color-hoverbtn-light-blue;
      }
    }

    td {
      text-align: center;
      height: 3em;
    }

    th {
      min-width: 7em;
    }

    &__head {

      &__titles {
        font-weight: bold;
        color: $color-blue-text;

        th {
          padding-bottom: 1.5em;
        }
      }

      &__subtitles {
        border-bottom: 1px solid #C4C4C4;
        font-weight: normal;

        th {
          min-width: 8em;
          padding-bottom: 0.3em;
          cursor: pointer;

          .active {
            background: $color-hoverbtn-light-blue;
          }

          &:hover {
            background: $color-hoverbtn-blue;
          }
        }
      }
    }
  }
  &__border-right {
    border-right: 1px solid #C4C4C4;
  }
}
</style>
